import { useRef, useState } from 'react';
import { api, apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/** What the file picker offers. */
const ACCEPT = 'image/png,image/jpeg,image/webp';

/** A sane ceiling on the *source* file, purely to refuse something absurd. */
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/** The square the picture is reduced to before it is sent. */
const TARGET_PX = 512;

/**
 * Shrink the chosen file to a square JPEG before uploading.
 *
 * A photograph off a phone is three to eight megabytes, and the API accepts
 * two — so the honest fix is not a larger limit on the server but a smaller
 * file from the browser. It is displayed at 84px at most, so anything beyond a
 * 512px square is bytes nobody will ever see.
 *
 * Centre-cropped to a square first, because the image is shown in a circle and
 * a letterboxed portrait would lose its subject to the mask anyway.
 */
async function toSquareJpeg(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('That file could not be read as an image.'));
      element.src = url;
    });

    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const size = Math.min(TARGET_PX, side);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(
      image,
      (image.naturalWidth - side) / 2,
      (image.naturalHeight - side) / 2,
      side,
      side,
      0,
      0,
      size,
      size,
    );

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) return file;
    return new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Upload or remove your profile picture.
 *
 * The file goes up as multipart rather than through the content library: that
 * route needs CONTENT_UPLOAD, which a learner neither has nor should need to
 * change their own picture.
 *
 * `fetch` is used directly rather than the shared `api` helper because the
 * helper sets a JSON content type, and a multipart body must be allowed to
 * declare its own boundary.
 */
export function AvatarPicker() {
  const { user, refresh } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'SX';

  async function choose(file: File | undefined) {
    if (!file) return;
    setError(null);

    if (!ACCEPT.split(',').includes(file.type)) {
      setError('Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError('That file is unusually large. Choose a photograph under 25MB.');
      return;
    }

    setBusy(true);
    try {
      // Reduced here rather than rejected: a camera photograph is several
      // megabytes and there is no reason to make someone resize it by hand.
      const upload = await toSquareJpeg(file);

      const body = new FormData();
      body.append('file', upload);
      const response = await apiFetch('/me/avatar', {
        method: 'POST',
        body,
      });
      if (!response.ok) {
        const detail = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? 'The picture could not be uploaded.');
      }
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
      // Cleared so choosing the same file again still fires a change event.
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api('/me/avatar', { method: 'DELETE' });
      await refresh();
    } catch {
      setError('The picture could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="avatarpick">
      <span className="avatarpick__preview">
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} alt="" />
        ) : (
          <span className="avatarpick__initials" aria-hidden="true">
            {initials}
          </span>
        )}
      </span>

      <div className="avatarpick__side">
        <p className="avatarpick__hint">
          PNG, JPEG or WebP. Large photographs are fine — they are resized and centre-cropped
          to a square in your browser before being sent.
        </p>

        <div className="avatarpick__row">
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => input.current?.click()}
            disabled={busy}
          >
            {busy ? 'Working…' : user?.avatarUrl ? 'Change picture' : 'Upload a picture'}
          </button>

          {user?.avatarUrl ? (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => void remove()}
              disabled={busy}
            >
              Remove
            </button>
          ) : null}
        </div>

        {error ? (
          <p className="avatarpick__error" role="alert">
            {error}
          </p>
        ) : null}

        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="avatarpick__input"
          onChange={(event) => void choose(event.target.files?.[0])}
        />
      </div>
    </div>
  );
}
